import React, { useEffect, useState } from "react";
import "../../../src/components/common/style.css";
import "../../../src/pages/blog/blog_list/blogList.css";
import "../../../src/components/common/responsive.css";
import "../../../src/pages/blog/blog_list/blog_responsive.css";
import "./update_responsive.css";

import FeaturedUpdate from "../../components/updates/FeaturedUpdate";
import UpdateCard from "../../components/updates/UpdateCard";
import MoreUpdateItem from "../../components/updates/MoreUpdateItem";
import { useApi } from "../../shared/api";
import { changeTitleAndMetaTags } from "../../shared/meta/metaTags";
import type { API } from "../../shared/api/configuration";

import SkeletonFeaturedUpdate from "../../components/skeleton/SkeletonFeaturedUpdate";
import SkeletonUpdateCard from "../../components/skeleton/SkeletonUpdateCard";
import SkeletonMoreUpdateItem from "../../components/skeleton/SkeletonMoreUpdateItem";

interface UpdateProps {
  event_id: number;
  event_title: string;
  event_img: string;
  event_category: string;
  thumbnail_image: string;
  event_date: string;
  slug: string;
}

interface CategoryProps {
  event_category: string;
  event_category_slug: string;
}

const LIMIT = 10;
const VIEW_MORE_STEP = 3;

const UpdateListPage: React.FC = () => {
  const API = useApi();

  const [updates, setUpdates] = useState<UpdateProps[]>([]);
  const [categories, setCategories] = useState<CategoryProps[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(VIEW_MORE_STEP);
  const [total, setTotal] = useState(0);

  /* ================= SEO ================= */
  const fetchSeoMetaDetails = async (API: API) => {
    try {
      const code = "update_page";
      const response = await API.API_V1.getSeoMetaDetails(code);
      await changeTitleAndMetaTags(response.data.result.page_title, {
        description: response.data.result.meta_description,
        keywords: response.data.result.meta_tag,
        cronical_url: window.location.href,
      });
    } catch (error) {}
  };

  /* ================= FETCH CATEGORIES ================= */
  const fetchCategories = async () => {
    try {
      const res = await API.API_V1.update.getUpdateCategories();
      if (res.data.success) {
        setCategories(res.data.categories);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  /* ================= FETCH UPDATES ================= */
  const fetchUpdates = async (
    pageNo: number,
    category: string | null,
    append = false
  ) => {
    try {
      const res = await API.API_V1.update.getUpdateList(
        pageNo,
        LIMIT,
        category
      );

      if (res.data.success) {
        setTotal(res.data.total);
        setPage(pageNo);

        setUpdates(prev =>
          append ? [...prev, ...res.data.updates] : res.data.updates
        );
      }
    } catch (err) {
      console.error("Error fetching updates:", err);
    }
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchUpdates(1, null);
      await fetchCategories();
      await fetchSeoMetaDetails(API);
      setLoading(false);
    };

    init();
  }, [API]);

  /* ================= CATEGORY CLICK ================= */
  const handleCategoryClick = async (slug: string | null) => {
    setLoading(true);
    setActiveCategory(slug);
    setUpdates([]);
    setVisibleCount(VIEW_MORE_STEP);
    await fetchUpdates(1, slug);
    setLoading(false);
  };

  /* ================= VIEW MORE ================= */
  const handleViewMore = async () => {
    const nextVisible = visibleCount + VIEW_MORE_STEP;
    setVisibleCount(nextVisible);

    if (nextVisible + 7 > updates.length && updates.length < total) {
      setLoadingMore(true);
      await fetchUpdates(page + 1, activeCategory, true);
      setLoadingMore(false);
    }
  };

  /* ================= EMPTY STATE ================= */
  if (!loading && !updates.length) {
    return <p className="text-center py-5">No updates available.</p>;
  }

  /* ================= DATA SPLIT ================= */
  const featured = updates[0];
  const gridUpdates = updates.slice(1, 7);
  const moreUpdates = updates.slice(7, 7 + visibleCount);

  return (
    <>
      {/* ================= BREADCRUMB ================= */}
      <div className="section_breadcrumb">
        <div className="container">
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#">Home</a>
            </li>
            <li className="breadcrumb-item active">Updates</li>
          </ul>
        </div>
      </div>

      {/* ================= FILTER ================= */}
      <section className="blog_filters">
        <div className="blog_filter container px-0">
          <div className="d-flex filter_btn_wrap">

            <div className="fix_btn">
              <button
                className={`filter-btn ${activeCategory === null ? "active" : ""}`}
                onClick={() => handleCategoryClick(null)}
              >
                All
              </button>
            </div>

            <div className="scroll_btns scroll-container">
              {categories.map(cat => (
                <button
                  key={cat.event_category_slug}
                  className={`filter-btn ${
                    activeCategory === cat.event_category_slug ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(cat.event_category_slug)}
                >
                  {cat.event_category}
                </button>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ================= FEATURED ================= */}
      <section className="blog_listing update_top_image">
        <div className="container">
          <h1 className="update_page_heading">Latest Updates</h1>
          {loading ? (
            <SkeletonFeaturedUpdate />
          ) : (
            featured && <FeaturedUpdate featured={featured} />
          )}
        </div>
      </section>

      {/* ================= LISTING ================= */}
      <section className="update_box_section">
        <div className="container">
          <div className="updates_listing_wrap">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonUpdateCard key={i} />
                ))
              : gridUpdates.map(item => (
                  <UpdateCard key={item.event_id} item={item} />
                ))}
          </div>

          {moreUpdates.length > 0 && (
            <>
              <div className="more_update_wrap">
                <h1 className="update_page_heading">More Updates</h1>

                {loading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <SkeletonMoreUpdateItem key={i} />
                    ))
                  : moreUpdates.map(item => (
                      <MoreUpdateItem key={item.event_id} item={item} />
                    ))}
              </div>

              {updates.length < total && (
                <div className="text-center vm">
                  <button
                    className="btn btn-success"
                    onClick={handleViewMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : "View More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default UpdateListPage;
